import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Users, Mail, Lock, User, Phone, ArrowLeft, Loader2, Info, Cake, GraduationCap, MapPin, UserPlus, Globe, Building2, DoorOpen, CheckCircle, AlertCircle } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';

export default function RegisterInvitedTenantPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, setLanguage, languages } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showExtendedFields, setShowExtendedFields] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Extended profile fields
    birthday: '',
    occupation: '',
    is_student: false,
    student_id: '',
    school_name: '',
    home_address: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please use the link from your invitation email or ask your landlord for a new invitation.');
      setLoading(false);
      return;
    }
    
    // Validate the invitation token
    const validateInvitation = async () => {
      try {
        const response = await authAPI.getPendingTenantInvitation(token);
        setInvitationData(response.data);
        
        // Pre-fill form data from invitation
        setFormData(prev => ({
          ...prev,
          name: response.data.name || '',
          email: response.data.email || '',
          phone: response.data.phone || ''
        }));
        
        setLoading(false);
      } catch (error) {
        console.error('Invalid invitation:', error);
        setError(error.response?.data?.detail || 'Invalid or expired invitation. Please request a new invitation from your landlord.');
        setLoading(false);
      }
    };
    
    validateInvitation();
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('errors.passwordTooShort'));
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    try {
      setSubmitting(true);
      const registrationData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: 'tenant',
        phone: formData.phone || null,
        invitation_token: token,
        // Extended profile data
        tenant_profile: {
          birthday: formData.birthday || null,
          school_name: formData.school_name || null,
          student_id: formData.student_id || null,
          home_address: formData.home_address || null,
          emergency_contact: {
            name: formData.emergency_contact_name || null,
            relationship: formData.emergency_contact_relationship || null,
            phone: formData.emergency_contact_phone || null,
            address: formData.emergency_contact_address || null,
          }
        }
      };
      
      await authAPI.register(registrationData);
      
      // Auto-login on successful registration
      await login(formData.email, formData.password);
      toast.success(t('auth.registrationSuccess'));
      navigate('/tenant');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#C85A42]/10 via-background to-[#6B8E6B]/10">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#C85A42]/10 via-background to-[#6B8E6B]/10 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-xl text-red-600 dark:text-red-400">Invalid Invitation</CardTitle>
            <CardDescription className="mt-2">{error}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/register/tenant')}
            >
              Register with Landlord Code
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#C85A42]/10 via-background to-[#6B8E6B]/10 p-4" data-testid="register-invited-tenant-page">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-between items-center mb-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="text-muted-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back')}
            </Button>
            
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[140px]">
                <Globe className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>{lang.nativeName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="mx-auto w-16 h-16 bg-[#C85A42]/10 rounded-full flex items-center justify-center mb-2">
            <UserPlus className="w-8 h-8 text-[#C85A42]" />
          </div>
          
          <CardTitle className="text-2xl font-bold">{t('auth.completeRegistration')}</CardTitle>
          <CardDescription>{t('auth.youHaveBeenInvited')}</CardDescription>
          
          {/* Invitation Info Card */}
          {invitationData && (
            <div className="bg-emerald-50 dark:bg-emerald-950/30 p-4 rounded-lg mt-4 text-left border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-2 mb-3 text-emerald-700 dark:text-emerald-300">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Invitation Valid</span>
              </div>
              <div className="space-y-2 text-sm">
                {invitationData.landlord && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span><strong>Landlord:</strong> {invitationData.landlord.name}</span>
                  </div>
                )}
                {invitationData.property && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    <span><strong>Property:</strong> {invitationData.property.name}</span>
                  </div>
                )}
                {invitationData.unit && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DoorOpen className="w-4 h-4" />
                    <span><strong>Unit:</strong> Room {invitationData.unit.unit_number}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info - Pre-filled from invitation */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  {t('common.name')}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('auth.fullName')}
                  required
                  data-testid="invited-tenant-name-input"
                />
              </div>
              
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  {t('common.phone')}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+63 912 345 6789"
                  data-testid="invited-tenant-phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                {t('common.email')}
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.emailPlaceholder')}
                required
                disabled={!!invitationData?.email}
                className={invitationData?.email ? "bg-muted" : ""}
                data-testid="invited-tenant-email-input"
              />
              {invitationData?.email && (
                <p className="text-xs text-muted-foreground">Email from invitation cannot be changed</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  {t('auth.password')}
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.createPassword')}
                  required
                  data-testid="invited-tenant-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  {t('auth.confirmPassword')}
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t('auth.confirmPassword')}
                  required
                  data-testid="invited-tenant-confirm-password-input"
                />
              </div>
            </div>
            
            {/* Extended Profile Toggle */}
            <div className="pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setShowExtendedFields(!showExtendedFields)}
              >
                <Info className="w-4 h-4 mr-2" />
                {showExtendedFields ? t('auth.hideOptionalFields') : t('auth.showOptionalFields')}
              </Button>
            </div>
            
            {showExtendedFields && (
              <div className="space-y-4 pt-4 border-t animate-in slide-in-from-top-4">
                <p className="text-sm text-muted-foreground">{t('auth.optionalFieldsDescription')}</p>

                {/* Personal Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="birthday" className="flex items-center gap-2">
                      <Cake className="w-4 h-4 text-muted-foreground" />
                      Date of Birth
                    </Label>
                    <Input
                      id="birthday"
                      name="birthday"
                      type="date"
                      value={formData.birthday}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="school_name" className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-muted-foreground" />
                      School / University
                    </Label>
                    <Input
                      id="school_name"
                      name="school_name"
                      value={formData.school_name}
                      onChange={handleChange}
                      placeholder="e.g., UP Diliman"
                    />
                  </div>
                </div>

                {/* Student ID */}
                <div className="space-y-2">
                  <Label htmlFor="student_id" className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-muted-foreground" />
                    Student ID Number
                  </Label>
                  <Input
                    id="student_id"
                    name="student_id"
                    value={formData.student_id}
                    onChange={handleChange}
                    placeholder="e.g., 2024-12345"
                  />
                </div>

                {/* Complete Address */}
                <div className="space-y-2">
                  <Label htmlFor="home_address" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    Complete Address
                  </Label>
                  <Input
                    id="home_address"
                    name="home_address"
                    value={formData.home_address}
                    onChange={handleChange}
                    placeholder="Street, City, Province, ZIP"
                  />
                </div>

                {/* Emergency Contact */}
                <div className="space-y-3 p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                  <Label className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <Users className="w-4 h-4" />
                    {t('tenants.emergencyContact')}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_name" className="text-sm">{t('common.name')}</Label>
                      <Input
                        id="emergency_contact_name"
                        name="emergency_contact_name"
                        value={formData.emergency_contact_name}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_relationship" className="text-sm">{t('tenants.relationship')}</Label>
                      <Input
                        id="emergency_contact_relationship"
                        name="emergency_contact_relationship"
                        value={formData.emergency_contact_relationship}
                        onChange={handleChange}
                        placeholder={t('auth.relationshipPlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_phone" className="text-sm">{t('common.phone')}</Label>
                      <Input
                        id="emergency_contact_phone"
                        name="emergency_contact_phone"
                        value={formData.emergency_contact_phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_address" className="text-sm">Contact Address</Label>
                    <Input
                      id="emergency_contact_address"
                      name="emergency_contact_address"
                      value={formData.emergency_contact_address}
                      onChange={handleChange}
                      placeholder="Street, City, Province, ZIP"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Checkbox
                id="terms-agreement"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                data-testid="terms-agreement-checkbox"
              />
              <label htmlFor="terms-agreement" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                I agree to the <Link to="/terms-of-service" target="_blank" className="text-[#C85A42] hover:underline font-medium">Terms of Service</Link> and <Link to="/privacy-policy" target="_blank" className="text-[#C85A42] hover:underline font-medium">Privacy Policy</Link>
              </label>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-[#C85A42] hover:bg-[#B54A35]"
              disabled={submitting || !agreedToTerms}
              data-testid="invited-tenant-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('auth.completeRegistration')}
                </>
              )}
            </Button>
            
            <p className="text-center text-sm text-muted-foreground">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-[#C85A42] hover:underline">
                {t('auth.login')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
